import { Controller } from "@hotwired/stimulus"

/**
 * Pricing Calculator Controller
 *
 * Real-time калькулятор стоимости подписки.
 * Отправляет данные на сервер для расчёта и обновляет UI.
 *
 * @targets
 *   - basePlan: radio buttons для базового плана
 *   - resourceInput: number inputs для ресурсов
 *   - resourceSlider: range inputs для ресурсов
 *   - channelInput: number inputs для каналов
 *   - marketplaceInput: number inputs для маркетплейсов
 *   - billingPeriod: select для периода оплаты
 *   - totalPrice: элемент с итоговой ценой
 *   - floorPrice, resourcesPrice, channelsPrice, marketplacesPrice: breakdown
 *   - loading: индикатор загрузки
 *   - breakdown: контейнер breakdown
 *
 * @values
 *   - calculateUrl: URL для API расчёта
 *   - resourcesConfig: JSON конфиг ресурсов
 *   - channelsConfig: JSON конфиг каналов
 *   - marketplacesConfig: JSON конфиг маркетплейсов
 */
export default class extends Controller {
  static targets = [
    "basePlan",
    "resourceInput",
    "resourceSlider",
    "channelInput",
    "marketplaceInput",
    "encryptionToggle",
    "whiteLabelToggle",
    "whiteLabelRow",
    "whiteLabelFree",
    "billingPeriod",
    "totalPrice",
    "floorPrice",
    "floorPriceRow",
    "resourcesBreakdown",
    "productsPrice",
    "customersPrice",
    "storagePrice",
    "staffPrice",
    "postsPrice",
    "resourcesPrice",
    "channelsPrice",
    "marketplacesPrice",
    "loading",
    "breakdown",
    "includedLabel"
  ]

  static values = {
    calculateUrl: String,
    resourcesConfig: Object,
    channelsConfig: Object,
    marketplacesConfig: Object,
    plansConfig: Array
  }

  // Debounce timer
  #debounceTimer = null
  #debounceDelay = 300 // ms

  connect() {
    console.log("PricingCalculator connected")
    // Обновляем лейблы для выбранного плана
    this.updateIncludedLabels()
    // Начальный расчёт
    this.calculate()
  }

  // ========== EVENT HANDLERS ==========

  onBasePlanChange(event) {
    this.updateIncludedLabels()
    this.calculate()
  }

  // Обновляет лейблы "бесплатно" на слайдерах при смене плана
  updateIncludedLabels() {
    const selectedPlan = this.basePlanTargets.find(r => r.checked)?.value
    if (!selectedPlan || !this.hasPlansConfigValue) return

    const plan = this.plansConfigValue.find(p => p.code === selectedPlan)
    const includedResources = plan?.included_resources || {}

    // Маппинг ресурсов: UI key → plan key
    const resourceMapping = {
      'products': 'products',
      'customers': 'customers',
      'storage_gb': 'storage_gb',
      'staff': 'staff',
      'posts': 'posts'
    }

    this.includedLabelTargets.forEach(label => {
      const resource = label.dataset.resource
      const planKey = resourceMapping[resource] || resource
      const included = includedResources[planKey] || 0

      const valueSpan = label.querySelector('[data-included-value]')
      if (valueSpan) {
        valueSpan.textContent = included
      }

      // Показываем/скрываем лейбл
      if (included > 0) {
        label.classList.remove('hidden')
      } else {
        label.classList.add('hidden')
      }
    })
  }

  onSliderChange(event) {
    const slider = event.target
    const resource = slider.dataset.resource

    // Синхронизируем с input
    const input = this.resourceInputTargets.find(i => i.dataset.resource === resource)
    if (input) {
      input.value = slider.value
    }

    this.debouncedCalculate()
  }

  onInputChange(event) {
    const input = event.target
    const resource = input.dataset.resource

    if (resource) {
      // Синхронизируем со slider если есть
      const slider = this.resourceSliderTargets.find(s => s.dataset.resource === resource)
      if (slider) {
        slider.value = input.value
      }
    }

    this.debouncedCalculate()
  }

  onPeriodChange(event) {
    this.applyPeriodDiscount()
  }

  incrementChannel(event) {
    const channel = event.currentTarget.dataset.channel
    const input = this.channelInputTargets.find(i => i.dataset.channel === channel)
    if (input) {
      const max = parseInt(input.max) || 10
      const current = parseInt(input.value) || 0
      if (current < max) {
        input.value = current + 1
        this.debouncedCalculate()
      }
    }
  }

  decrementChannel(event) {
    const channel = event.currentTarget.dataset.channel
    const input = this.channelInputTargets.find(i => i.dataset.channel === channel)
    if (input) {
      const current = parseInt(input.value) || 0
      if (current > 0) {
        input.value = current - 1
        this.debouncedCalculate()
      }
    }
  }

  // ========== CALCULATION ==========

  debouncedCalculate() {
    clearTimeout(this.#debounceTimer)
    this.#debounceTimer = setTimeout(() => this.calculate(), this.#debounceDelay)
  }

  async calculate() {
    this.showLoading()

    const data = this.collectFormData()

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const result = await response.json()

      if (result.success) {
        this.updateUI(result)
      } else {
        console.error("Calculation failed:", result.error)
      }
    } catch (error) {
      console.error("Calculate error:", error)
    } finally {
      this.hideLoading()
    }
  }

  collectFormData() {
    // Base plan
    const basePlanInput = this.basePlanTargets.find(r => r.checked)
    const basePlan = basePlanInput?.value || 'custom'

    // Resources
    const resources = {}
    this.resourceInputTargets.forEach(input => {
      const resource = input.dataset.resource
      const value = parseInt(input.value) || 0
      if (value > 0) {
        resources[resource] = value
      }
    })

    // Channels
    const channels = {}
    this.channelInputTargets.forEach(input => {
      const channel = input.dataset.channel
      const value = parseInt(input.value) || 0
      if (value > 0) {
        channels[channel] = value
      }
    })

    // Marketplaces
    const marketplaces = {}
    this.marketplaceInputTargets.forEach(input => {
      const mp = input.dataset.marketplace
      const orders = parseInt(input.value) || 0
      if (orders > 0) {
        marketplaces[mp] = { orders }
      }
    })

    // Modifiers (encryption etc)
    const modifiers = {}
    if (this.hasEncryptionToggleTarget && this.encryptionToggleTarget.checked) {
      modifiers.storage_encryption = true
    }

    // Modules (white_label etc)
    const modules = {}
    if (this.hasWhiteLabelToggleTarget && this.whiteLabelToggleTarget.checked) {
      modules.white_label = true
    }

    return {
      base_plan: basePlan,
      resources,
      modules,
      channels,
      marketplaces,
      modifiers
    }
  }

  // ========== UI UPDATES ==========

  updateUI(result) {
    // Итого
    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.textContent = result.total_price_formatted || '0 ₽'
    }

    // Breakdown
    const breakdown = result.breakdown || {}
    const resources = breakdown.resources || {}

    // Floor price (скрываем если 0)
    if (this.hasFloorPriceTarget) {
      const floor = breakdown.floor_price_minor || 0
      this.floorPriceTarget.textContent = this.formatPrice(floor)
      if (this.hasFloorPriceRowTarget) {
        this.floorPriceRowTarget.classList.toggle('hidden', floor === 0)
      }
    }

    // Детализация ресурсов
    if (this.hasProductsPriceTarget) {
      this.productsPriceTarget.textContent = this.formatPrice(resources.products?.price_minor || 0)
    }
    if (this.hasCustomersPriceTarget) {
      this.customersPriceTarget.textContent = this.formatPrice(resources.customers?.price_minor || 0)
    }
    if (this.hasStoragePriceTarget) {
      this.storagePriceTarget.textContent = this.formatPrice(resources.storage_gb?.price_minor || 0)
    }
    if (this.hasStaffPriceTarget) {
      this.staffPriceTarget.textContent = this.formatPrice(resources.staff?.price_minor || 0)
    }
    if (this.hasPostsPriceTarget) {
      this.postsPriceTarget.textContent = this.formatPrice(resources.posts?.price_minor || 0)
    }

    // Общая сумма ресурсов
    if (this.hasResourcesPriceTarget) {
      const total = Object.values(resources).reduce((sum, r) => sum + (r.price_minor || 0), 0)
      this.resourcesPriceTarget.textContent = this.formatPrice(total)
    }

    if (this.hasChannelsPriceTarget) {
      const channels = breakdown.channels || {}
      const total = Object.values(channels).reduce((sum, c) => sum + (c.total_price_minor || 0), 0)
      this.channelsPriceTarget.textContent = this.formatPrice(total)
    }

    if (this.hasMarketplacesPriceTarget) {
      const mps = breakdown.marketplaces || {}
      const total = Object.values(mps).reduce((sum, m) => sum + (m.price_minor || 0), 0)
      this.marketplacesPriceTarget.textContent = this.formatPrice(total)
    }

    // WhiteLabel free when > 300k
    if (this.hasWhiteLabelFreeTarget) {
      const subtotal = result.total_price_minor || 0
      const isFreeWhiteLabel = subtotal >= 30000000 // 300k в копейках
      this.whiteLabelFreeTarget.classList.toggle('hidden', !isFreeWhiteLabel)

      // Автоматически включаем WhiteLabel если > 300k
      if (isFreeWhiteLabel && this.hasWhiteLabelToggleTarget) {
        this.whiteLabelToggleTarget.checked = true
      }
    }

    // Apply period discount if selected
    this.applyPeriodDiscount()
  }

  applyPeriodDiscount() {
    // TODO: реализовать скидки за период
    // Сейчас просто показываем месячную цену
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  // ========== HELPERS ==========

  formatPrice(minorUnits) {
    if (!minorUnits || minorUnits === 0) return '0 ₽'
    const rubles = minorUnits / 100
    return new Intl.NumberFormat('ru-RU').format(rubles) + ' ₽'
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
